import {
  formatDateForSheet,
  normalizeGoogleCalendarColorId,
  parseEventDateList,
  parseEventDateStart,
  parseDepositSchedule,
  serializeDepositSchedule,
  sumDepositSchedule,
  getDepositPaidTotal,
  parseEventDayTimes,
  serializeEventDayTimes,
  type DepositInstallment,
  type EventDayTimes,
} from '@qjob/shared';
import { JOB_STATUS_AWAITING } from './utils';

export const WORKING_TIME_PRESETS = [
  { key: 'full-day', label: 'เต็มวัน', start: '07:00', end: '17:00' },
  { key: 'morning', label: 'ครึ่งเช้า', start: '07:00', end: '11:00' },
  { key: 'afternoon', label: 'ครึ่งบ่าย', start: '13:00', end: '17:00' },
  { key: 'night', label: 'กลางคืน', start: '18:00', end: '22:00' },
] as const;

export type JobFormRecord = Record<string, any>;

export function getEmptyJobForm(): JobFormRecord {
  return {
    JobID: '',
    Client: '',
    'Contact Name': '',
    Phone: '',
    JobType: '',
    'Job Name': '',
    PersonalUse: false,
    'Event Date': '',
    _eventDatesList: [] as string[],
    'Payment Date': '',
    'Payment Due Date': '',
    'Item / Job Title': '',
    'Price per Unit': '',
    'Actual Received': '',
    'Deposit Schedule': '',
    'Payment Ledger': '',
    // New jobs stay on the familiar payment form until the user explicitly
    // enables deposit tracking for that job.
    'Deposit Enabled': false,
    'Payment Terms': '',
    'Document History': '',
    'Event Day Times': '',
    Status: JOB_STATUS_AWAITING,
    'Slip Reference': '',
    Notes: '',
    'Working Time': '',
    Location: '',
    'Google Maps Link': '',
    _rowIndex: null,
    'Create Quotation': false,
    'Quotation Date': '',
    'Create Receipt': false,
    'Quotation Description': '',
    'Quotation Company Detail': '',
    'Include Signature': true,
    'Show Provider Name on Quotation': true,
    'Apply Tax': true,
    'Show Payment Due on Quotation': true,
    'Payment Due Mode': 'auto',
    'Document Language': 'th',
    'Quotation Number': '',
    'Quotation URL': '',
    'Quotation PDF URL': '',
    'Receipt Number': '',
    'Receipt URL': '',
    'Receipt PDF URL': '',
    'Invoice Number': '',
    'Invoice PDF URL': '',
    Reminders: '',
    Color: '',
    _draftId: '',
  };
}

export { parseDepositSchedule, serializeDepositSchedule, sumDepositSchedule, getDepositPaidTotal, parseEventDayTimes, serializeEventDayTimes };
export type { DepositInstallment, EventDayTimes };

export function loadJobFormFromRecord(job: JobFormRecord): JobFormRecord {
  const base = getEmptyJobForm();
  const merged = { ...base, ...job };
  applyEventDatesToForm(merged, job['Event Date']);
  merged['Payment Date'] = dateToInputFormat(job['Payment Date']);
  merged['Payment Due Date'] = dateToInputFormat(job['Payment Due Date']);
  merged['Quotation Date'] = dateToInputFormat(job['Quotation Date']);
  merged['Include Signature'] = toBool(merged['Include Signature'], true);
  merged['Show Provider Name on Quotation'] = toBool(merged['Show Provider Name on Quotation'], true);
  merged['Apply Tax'] = toBool(merged['Apply Tax'], true);
  const storedDepositToggle = job['Deposit Enabled'];
  const hasStoredDepositToggle = storedDepositToggle === true || storedDepositToggle === false
    || ['true', 'false', '1', '0', 'yes', 'no', 'on', 'off'].includes(String(storedDepositToggle ?? '').trim().toLowerCase());
  const hasDepositHistory = !!String(job['Deposit Schedule'] ?? '').trim()
    || !!String(job['Payment Ledger'] ?? '').trim();
  // Preserve the deposit UI for old jobs which already have deposit data,
  // while jobs without that data keep the legacy payment form by default.
  merged['Deposit Enabled'] = hasStoredDepositToggle
    ? toBool(storedDepositToggle, false)
    : hasDepositHistory;
  merged['Show Payment Due on Quotation'] = toBool(merged['Show Payment Due on Quotation'], true);
  // Document checkboxes are one-shot UI actions for the dedicated PDF button,
  // not persistent auto-generation settings. Never re-select them when an
  // existing job is opened, otherwise generating a receipt can also recreate
  // an old quotation/invoice unintentionally.
  merged['Create Quotation'] = false;
  merged['Create Receipt'] = false;
  merged['Document Language'] = String(merged['Document Language'] || 'th').toLowerCase() === 'en' ? 'en' : 'th';
  merged['Color'] = normalizeGoogleCalendarColorId(merged['Color']);
  return merged;
}

export function toBool(value: unknown, defaultValue: boolean): boolean {
  if (value === true || value === false) return value;
  const raw = String(value ?? '').trim().toLowerCase();
  if (!raw) return defaultValue;
  return raw === 'true' || raw === '1' || raw === 'yes' || raw === 'on';
}

export function inferPaymentDueMode(job: JobFormRecord): 'auto' | 'manual' | 'none' {
  const mode = String(job['Payment Due Mode'] || '').trim().toLowerCase();
  if (mode === 'none' || mode === 'no-reminder' || mode === 'ไม่ต้องแจ้งเตือน') return 'none';
  if (mode === 'manual' || mode === 'กำหนดเอง') return 'manual';
  if (mode === 'auto' || mode === 'อัตโนมัติ') return 'auto';
  const due = parseEventDateStart(job['Payment Due Date']);
  const event = parseEventDateStart(job['Event Date']);
  if (!due || !event) return 'auto';
  const auto = new Date(event);
  auto.setMonth(auto.getMonth() + 1);
  return sameCalendarDate(due, auto) ? 'auto' : 'manual';
}

function sameCalendarDate(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export function formatDateForInput(dateValue: Date | null): string {
  if (!dateValue || isNaN(dateValue.getTime())) return '';
  const y = dateValue.getFullYear();
  const m = String(dateValue.getMonth() + 1).padStart(2, '0');
  const d = String(dateValue.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function computeAutoPaymentDueInput(eventDateRaw: string): string {
  const event = parseEventDateStart(eventDateRaw);
  if (!event) return '';
  const auto = new Date(event);
  auto.setMonth(auto.getMonth() + 1);
  return formatDateForInput(auto);
}

export function getWorkingTimeInputPart(value: string, part: 'start' | 'end'): string {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const explicitParts = raw.split(/\s+-\s+/);
  if (explicitParts.length > 1) {
    return String(part === 'end' ? explicitParts[1] : explicitParts[0]).trim();
  }
  const matches = raw.match(/\d{1,2}[:.]\d{2}|\b\d{3,4}\b/g) || [];
  if (matches.length > 1) return part === 'end' ? (matches[1] ?? '') : (matches[0] ?? '');
  if (matches.length === 1) return part === 'start' ? (matches[0] ?? '') : '';
  return part === 'start' ? raw : '';
}

export function normalizeWorkingTimePart(value: string): string {
  const raw = String(value || '').trim().replace(/[–—−]/g, '-');
  if (!raw) return '';
  // Accept a manually typed time even when the user adds a suffix such as
  // "น." or leaves a little whitespace while moving between the two fields.
  const colonMatch = raw.match(/^(\d{1,2})\s*[:.]\s*(\d{1,2})/);
  const compactMatch = raw.match(/^(\d{3,4})$/);
  const hourOnlyMatch = raw.match(/^(\d{1,2})(?:\s*(?:น\.?|โมง))?$/i);
  let hour = '';
  let minute = '';
  if (colonMatch) {
    hour = colonMatch[1];
    minute = colonMatch[2];
  } else if (compactMatch) {
    const digits = compactMatch[1];
    hour = digits.length === 3 ? digits.slice(0, 1) : digits.slice(0, 2);
    minute = digits.length === 3 ? digits.slice(1) : digits.slice(2);
  } else if (hourOnlyMatch) {
    hour = hourOnlyMatch[1];
    minute = '00';
  } else return '';
  const hourNum = Number(hour);
  const minuteNum = Number(minute);
  if (isNaN(hourNum) || isNaN(minuteNum) || hourNum < 0 || hourNum > 23 || minuteNum < 0 || minuteNum > 59) return '';
  return String(hourNum).padStart(2, '0') + ':' + String(minuteNum).padStart(2, '0');
}

export function getWorkingTimePart(value: string, part: 'start' | 'end'): string {
  const raw = getWorkingTimeInputPart(value, part);
  if (!raw) return '';
  return normalizeWorkingTimePart(raw);
}

export function setWorkingTimePart(current: string, part: 'start' | 'end', value: string): string {
  const start = part === 'start' ? value : getWorkingTimeInputPart(current, 'start');
  const end = part === 'end' ? value : getWorkingTimeInputPart(current, 'end');
  return start && end ? `${start} - ${end}` : start || end || '';
}

export function normalizeWorkingTimeInput(value: string): string {
  const start = getWorkingTimePart(value, 'start');
  const end = getWorkingTimePart(value, 'end');
  if (start && end) return `${start} - ${end}`;
  return value;
}

export function validateWorkingTime(value: string): boolean {
  const raw = String(value || '').trim();
  if (!raw) return true;
  const start = getWorkingTimePart(value, 'start');
  const end = getWorkingTimePart(value, 'end');
  return !!(start && end);
}

export function calculateQuotedTotal(pricePerUnit: unknown, applyTax: boolean, taxPercent: number): number {
  const net = Number(String(pricePerUnit ?? '').replace(/,/g, '').trim());
  if (isNaN(net) || net <= 0) return 0;
  // Price per Unit is the cash net total, matching the PDF. WHT is displayed
  // separately, not added back into the amount customers must transfer.
  return Math.round(net * 100) / 100;
}

export interface ClientTemplate {
  label: string;
  clientName?: string;
  tel?: string;
  detail?: string;
}

export function composeClientCompanyDetail(client: ClientTemplate): string {
  const detail = String(client.detail || '').trim();
  const tel = String(client.tel || '').trim();
  const lines = detail ? detail.split('\n').map((l) => l.trim()).filter(Boolean) : [];
  if (tel && !lines.some((l) => /^tel\s*:/i.test(l))) lines.push('Tel: ' + tel);
  return lines.join('\n');
}

export function dateToInputFormat(value: unknown): string {
  const d = parseEventDateStart(value);
  return d && !isNaN(d.getTime()) ? formatDateForInput(d) : '';
}

// Populates both the single "earliest date" anchor (`Event Date`, used for
// required-field validation and payment-due-date auto-calc) and the full,
// possibly non-contiguous, set of selected days (`_eventDatesList`, drives
// the multi-select date picker).
export function applyEventDatesToForm(form: JobFormRecord, eventDateRaw: unknown): void {
  const days = parseEventDateList(eventDateRaw);
  if (!days.length) {
    form['Event Date'] = '';
    form['_eventDatesList'] = [];
    return;
  }
  const inputs = days.map((d) => formatDateForInput(d));
  form['_eventDatesList'] = inputs;
  form['Event Date'] = inputs[0];
}

export function formatDateToSheet(value: unknown): string {
  if (!value) return '';
  const d = parseEventDateStart(value);
  if (!d || isNaN(d.getTime())) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

// Serializes the picker's selected days back to the canonical stored string —
// a single "dd/MM/yyyy" for one day, otherwise a comma-separated list of every
// selected day (preserves gaps, e.g. "01/06/2026,02/06/2026,05/06/2026").
export function buildEventDateValue(form: JobFormRecord): string {
  const list: unknown = form['_eventDatesList'];
  const inputs = Array.isArray(list) ? list : [];
  const parsed = inputs
    .map((s) => parseEventDateStart(s))
    .filter((d): d is Date => !!d && !isNaN(d.getTime()));
  if (!parsed.length) return formatDateToSheet(form['Event Date']);

  const sorted = [...parsed].sort((a, b) => a.getTime() - b.getTime());
  const seen = new Set<string>();
  const unique: Date[] = [];
  for (const d of sorted) {
    const key = formatDateForSheet(d);
    if (!seen.has(key)) {
      seen.add(key);
      unique.push(d);
    }
  }
  if (unique.length === 1) return formatDateForSheet(unique[0]);
  return unique.map((d) => formatDateForSheet(d)).join(',');
}

export function useClientTemplate(form: JobFormRecord, client: ClientTemplate): void {
  if (client.clientName) form.Client = client.clientName;
  // Template "label" is the short call-name used on LINE (not the legal company name).
  if (client.label) form['Contact Name'] = client.label;
  const detail = composeClientCompanyDetail(client);
  if (detail) form['Quotation Company Detail'] = detail;
}
